/**
 * 인증 관련 유틸리티
 * KV Session 기반 인증 (HttpOnly Cookie)
 */

import { createContext, useContext, useState, useEffect } from 'react';

import { debugLog, debugWarn, debugError } from './debug.js';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authSuccess = params.get('auth_success');
    const authToken = params.get('auth_token');

    if (authSuccess && authToken) {
      // ⚠️ CRITICAL: 모바일 OAuth 로그인 핵심 로직 - 수정 시 모바일 테스트 필수!
      // 모바일 브라우저는 OAuth 리다이렉트 체인(Google/Kakao → callback)에서
      // Set-Cookie를 무시하므로, 일회용 토큰을 same-origin POST로 교환하여 쿠키 설정.
      // 이 코드를 제거하거나 LoginPage에서 auth_token 파라미터를 삭제하면 모바일 로그인 불가.
      debugLog('[Auth] Exchanging auth token for cookie...');
      fetch('/api/auth/exchange-token', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: authToken })
      }).then(res => {
        debugLog('[Auth] Token exchange result:', res.status);
        debugLog('[Auth] Cookies after exchange:', document.cookie);
        // URL 정리
        params.delete('auth_success');
        params.delete('auth_token');
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', newUrl);
        // 쿠키가 설정된 후 인증 상태 확인
        checkAuth();
      }).catch(err => {
        debugError('[Auth] Token exchange failed:', err);
        checkAuth();
      });
    } else if (authSuccess) {
      debugLog('[Auth] OAuth success detected:', authSuccess);
      params.delete('auth_success');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
      checkAuth();
    } else {
      checkAuth();
    }
  }, []);

  const checkAuth = async () => {
    try {
      debugLog('[Auth] checkAuth called, cookies:', document.cookie);
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      debugLog('[Auth] checkAuth response:', response.status);
      if (response.ok) {
        const data = await response.json();
        debugLog('[Auth] checkAuth user:', data.user?.email);
        setUser(data.user);
      } else {
        debugLog('[Auth] checkAuth failed:', response.status);
        setUser(null);
      }
    } catch (err) {
      debugError('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // HttpOnly Cookie 포함
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '로그인 실패');
    }

    const data = await response.json();

    // 로그인 API가 이미 사용자 정보를 반환하므로 바로 설정
    if (data.success && data.data?.user) {
      setUser(data.data.user);
    }

    return data;
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      debugError('Logout failed:', err);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
